import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KnittingMachine, KnittingMachineSearchRequest, KnittingMachineStatusOption } from '../../../models/knitting/knitting';
import { KnittingMachineProxyService } from '../../../proxies/production/knitting-machines.proxy.service';
import { EnumValues } from '@card-software/library';
import { EnumService } from '@app/services/enum.service';
import startCase from 'lodash/startCase';
import { AssetProxyService } from '@app/proxies/asset/asset.proxy.service';
import { Asset } from '@app/models/asset/asset';
import { KnittingOrder } from '@app/models/production/knitting-order';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import _ from 'lodash';
import { UsersProxyService } from '@app/proxies/users.proxy.service';
import { UserFull } from '../../../models/user-master/user-lite';
import { MessageService, SelectItem } from 'primeng/api';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { MachineDashboardRoles } from '@app/models/roles/roles-group';
import { Subject, finalize, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { FabricCategoriesProxyService } from '@app/proxies/item/fabric-categories.proxy.service';
import { FabricCategory } from '@app/models/item/fabric-categories/fabric-category';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { AuthService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-knitting-dashboard',
  templateUrl: './knitting-dashboard.component.html',
  styleUrl: './knitting-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KnittingDashboardComponent implements OnInit {
  isLoading = signal(0);

  private readonly knittingMachineProxyService = inject(KnittingMachineProxyService);
  private readonly assetProxyService = inject(AssetProxyService);
  private readonly enumService = inject(EnumService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly usersProxyService = inject(UsersProxyService);
  private readonly fabricCategoriesProxyService = inject(FabricCategoriesProxyService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  readonly localeHelperService = inject(LocaleHelperService);
  private readonly destroyRef = inject(DestroyRef);

  readonly statuses: EnumValues = this.enumService.getEnum('KnittingMachineStatus', 'productionEnums').values;
  readonly statusOptions: KnittingMachineStatusOption[] = Object.entries(this.statuses).map(([key, label]) => ({
    value: +key,
    label: startCase(label),
    icon: this.getIcon(label),
    color: this.getColor(label),
  }));

  readonly viewModeOptions = [
    { label: 'Grid', value: 'grid', icon: 'pi pi-th-large' },
    { label: 'List', value: 'list', icon: 'pi pi-list' },
  ];
  readonly machineTypeOptions: { label: string; value: string }[] = [
    { label: 'Single Knit', value: '67a69d43f67aa1c795113e55' },
    { label: 'Double Knit', value: '67acdba2ee06eac3505f985f' },
  ];
  readonly statusColorMap: Record<number, string> = {
    0: '#f7fafc',
    1: '#ebf8ff',
    2: '#eef2ff',
    3: '#f0fff4',
    4: '#e6fffa',
    5: '#fff5f5',
    6: '#fffaf0',
  };

  viewMode: 'grid' | 'list' = 'grid';
  showOperators = false;
  searchMachineId = '';

  blockSection = signal(false);
  blockAutoCompleteInputs = signal(false);

  private readonly _allAssets = signal<Asset[]>([]);
  private readonly _allAssetsForOptions = signal<Asset[]>([]);
  private readonly _knittingMachines = signal<KnittingMachine[]>([]);
  private readonly _selectedNeedles = signal<number[]>([]);
  private readonly _machineTypeValues = signal<string[]>(this.machineTypeOptions.map((o) => o.value));

  private readonly _assetMap = computed(() => new Map(this._allAssets().map((a) => [a.assetId, a])));
  private readonly _machinesMap = computed(() => new Map(this._knittingMachines().map((m) => [m.assetId, m])));

  readonly uniqueNeedleOptions = computed<SelectItem[]>(() => {
    const needles = this._allAssetsForOptions()
      .map((a) => (a.knittingMachineDetails?.cylinderNeedles ?? 0) + (a.knittingMachineDetails?.dialNeedles ?? 0))
      .filter((n): n is number => n != null);
    return _.sortBy(_.uniq(needles)).map((n) => ({ label: `${n}`, value: n }));
  });

  filteredKnittingMachines = computed(() => {
    const machines = this._knittingMachines();
    const assets = this._allAssets();
    if (assets.length === 0) return machines;
    const assetIds = new Set(assets.map((a) => a.assetId));
    return machines.filter((m) => assetIds.has(m.assetId));
  });

  get allAssets(): Asset[] {
    return this._allAssets();
  }
  set allAssets(value: Asset[]) {
    this._allAssets.set(value);
  }

  get selectedNeedles(): number[] {
    return this._selectedNeedles();
  }
  set selectedNeedles(value: number[]) {
    this._selectedNeedles.set(value);
    this.assetFilters$.next();
  }

  get knittingMachines(): KnittingMachine[] {
    return this._knittingMachines();
  }
  set knittingMachines(value: KnittingMachine[]) {
    this._knittingMachines.set(value);
  }

  get machineTypeValues(): string[] {
    return this._machineTypeValues();
  }
  set machineTypeValues(value: string[]) {
    this._machineTypeValues.set(value);
    this.assetFilters$.next();
  }

  readonly originalKnittingMachines = signal<KnittingMachine[]>([]);
  readonly originalAssets = signal<Asset[]>([]);

  private readonly _originalMachineMap = computed(() => new Map(this.originalKnittingMachines().map((m) => [m.assetId, m])));
  private readonly _originalAssetMap = computed(() => new Map(this.originalAssets().map((a) => [a.assetId, a])));

  fabricCategories: FabricCategory[] = [];
  operatorsList: UserFull[] = [];
  draggedOperator: UserFull | null = null;
  recordKnittingOrders: Record<string, KnittingOrder[]> = {};

  private readonly assetFilters$ = new Subject<void>();
  private readonly machineIdSearch$ = new Subject<string>();

  ngOnInit(): void {
    this.canEdit();
    this.setupDebounce();
    this.setupAssetFiltersDebounce();
    this.loadInitialData();
  }

  private addLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private removeLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  private setupDebounce(): void {
    this.machineIdSearch$
      .pipe(
        debounceTime(300),
        switchMap(() => {
          const searchRequest: KnittingMachineSearchRequest = {
            assetId: this.searchMachineId || undefined,
          };
          return this.knittingMachineProxyService.searchMachineDetail(searchRequest);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          if (result.success) {
            const machines = result.data ?? [];
            this.knittingMachines = machines;
            this.originalKnittingMachines.set(structuredClone(machines));
          }
        },
      });
  }

  private setupAssetFiltersDebounce(): void {
    this.assetFilters$
      .pipe(
        debounceTime(300),
        switchMap(() => {
          const machineTypes = this._machineTypeValues();
          const needles = this._selectedNeedles();
          return this.assetProxyService.filterAssets({
            assetId: 'KM',
            assetTypeReferences: machineTypes.length < this.machineTypeOptions.length ? machineTypes : undefined,
            totalNeedles: needles.length > 0 ? needles : undefined,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.allAssets = res.data ?? [];
          this.originalAssets.set(structuredClone(this.allAssets));
        },
      });
  }

  loadKnittingMachines(): void {
    const searchRequest: KnittingMachineSearchRequest = {
      assetId: this.searchMachineId || undefined,
    };

    this.addLoading();
    this.knittingMachineProxyService
      .searchMachineDetail(searchRequest)
      .pipe(finalize(() => this.removeLoading()))
      .subscribe({
        next: (result) => {
          if (result.success) {
            const machines = result.data ?? [];
            this.knittingMachines = machines;
            this.originalKnittingMachines.set(structuredClone(machines));
          }
        },
      });
  }

  onSearchFiltersChange(): void {
    this.machineIdSearch$.next(this.searchMachineId);
  }

  clearSearchFilters(): void {
    this.searchMachineId = '';
    this.loadKnittingMachines();
  }

  toggleOperators(): void {
    this.showOperators = !this.showOperators;
  }

  saveDetails(assetId: string): void {
    const currentMachine = this.knittingMachines.find((m) => m.assetId === assetId);
    const currentAsset = this.allAssets.find((a) => a.assetId === assetId);
    const originalMachine = this._originalMachineMap().get(assetId);
    const originalAsset = this._originalAssetMap().get(assetId);

    if (currentMachine && !_.isEqual(currentMachine, originalMachine)) {
      this.updateKnittingMachine(currentMachine);
    }
    if (currentAsset && !_.isEqual(currentAsset, originalAsset)) {
      this.updateAsset(currentAsset);
    }
  }

  updateKnittingMachine(knittingMachine: KnittingMachine): void {
    this.addLoading();
    this.knittingMachineProxyService
      .updateKnittingMachine(knittingMachine)
      .pipe(finalize(() => this.removeLoading()))
      .subscribe({
        next: () => {
          this.originalKnittingMachines.update((arr) => [
            ...arr.filter((m) => m.assetId !== knittingMachine.assetId),
            structuredClone(knittingMachine),
          ]);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('CommonMessages.Success'),
            detail: this.translateService.instant('knittingDashboardMessageDialogs.successUpdateKnitting'),
            key: 'br',
          });
        },
      });
  }

  updateAsset(asset: Asset): void {
    this.addLoading();
    this.assetProxyService
      .updateAsset(asset)
      .pipe(finalize(() => this.removeLoading()))
      .subscribe({
        next: () => {
          this.originalAssets.update((arr) => [...arr.filter((a) => a.assetId !== asset.assetId), structuredClone(asset)]);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('CommonMessages.Success'),
            detail: this.translateService.instant('knittingDashboardMessageDialogs.successUpdateAsset'),
            key: 'br',
          });
        },
      });
  }

  //#region initial calls
  private loadInitialData(): void {
    this.addLoading();
    forkJoin({
      machines: this.knittingMachineProxyService.searchMachineDetail({}).pipe(catchError(() => of(null))),
      assets: this.assetProxyService.filterAssets({ assetId: 'KM' }).pipe(catchError(() => of(null))),
      users: this.usersProxyService.getAllUsers().pipe(catchError(() => of(null))),
      fabricCategories: this.fabricCategoriesProxyService.get().pipe(catchError(() => of(null))),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.removeLoading()),
      )
      .subscribe({
        next: ({ machines, assets, users, fabricCategories }) => {
          if (machines?.success) {
            const machineData = machines.data ?? [];
            this.knittingMachines = machineData;
            this.originalKnittingMachines.set(structuredClone(machineData));
          }

          if (assets) {
            const assetData = assets.data ?? [];
            this.allAssets = assetData;
            this.originalAssets.set(structuredClone(assetData));
            this._allAssetsForOptions.set(assetData);
          }

          if (users?.success && users.data) {
            this.operatorsList = users.data;
          }

          if (fabricCategories?.success && fabricCategories.data) {
            this.fabricCategories = fabricCategories.data
              .filter((fc) => fc.level === 1)
              .map((fc) => ({
                ...fc,
                localizedName: this.localeHelperService.getLocalizedValue(fc.name),
              }));
          }
        },
      });
  }
  //#endregion

  knittingMachinedChanged(assetId: string): boolean {
    const originalMachine = this._originalMachineMap().get(assetId);
    const currentMachine = this._machinesMap().get(assetId);
    if (originalMachine && currentMachine && !_.isEqual(originalMachine, currentMachine)) return true;

    const originalAsset = this._originalAssetMap().get(assetId);
    const currentAsset = this._assetMap().get(assetId);
    if (originalAsset && currentAsset && !_.isEqual(originalAsset, currentAsset)) return true;

    return false;
  }

  resetKnittingMachine(assetId: string): void {
    const originalMachine = this._originalMachineMap().get(assetId);
    const currentMachine = this._machinesMap().get(assetId);
    const originalAsset = this._originalAssetMap().get(assetId);
    const currentAsset = this._assetMap().get(assetId);

    if (originalMachine && currentMachine && !_.isEqual(originalMachine, currentMachine)) {
      this.knittingMachines = this.knittingMachines.map((m) => (m.assetId === assetId ? structuredClone(originalMachine) : m));
    }
    if (originalAsset && currentAsset && !_.isEqual(originalAsset, currentAsset)) {
      this.allAssets = this.allAssets.map((a) => (a.assetId === assetId ? structuredClone(originalAsset) : a));
    }
  }

  isDoubleKnittingMachine(asset: Asset): boolean {
    return asset.assetTypeReference === environment.asset.doubleKnitAssetTypeId;
  }

  getAsset(assetId: string): Asset | undefined {
    return this._assetMap().get(assetId);
  }

  getKnittingOrdersByAssetId(assetId: string): void {
    this.rollProxyService.getKnittingOrderByAssetId(assetId).subscribe({
      next: (res) => {
        this.recordKnittingOrders[assetId] = res.data ?? [];
      },
      error: () => {
        this.recordKnittingOrders[assetId] = [];
      },
    });
  }

  getStatusBgClass(status: number): string {
    return this.statusColorMap[status] || 'bg-white';
  }

  getStatusBgColor(status: number): string {
    return this.statusColorMap[status] || '#ffffff';
  }

  //#region Drag and Drop Operators
  shiftChange(): void {
    this.knittingMachineProxyService.shiftMachines().subscribe({
      next: (machines) => {
        this.knittingMachines = machines;
      },
    });
  }

  removeMachineOperator(machineIndex: number, operatorName: string | null): void {
    this.knittingMachines[machineIndex].currentOperatorUserName = operatorName;
    this.updateKnittingMachine(this.knittingMachines[machineIndex]);
  }

  dragStart(operator: UserFull): void {
    this.draggedOperator = operator;
  }

  drop(machineIndex: number): void {
    if (this.draggedOperator) {
      this.removeMachineOperator(machineIndex, `${this.draggedOperator.profile.firstName} ${this.draggedOperator.profile.lastName}`);
      this.draggedOperator = null;
    }
  }

  dragEnd(): void {
    this.draggedOperator = null;
  }
  //#endregion

  private getIcon(label: string): string {
    switch (label) {
      case 'Idle':
        return 'pi-pause';
      case 'Loading':
        return 'pi-sync pi-spin';
      case 'Unloading':
        return 'pi-arrow-up';
      case 'InUse':
        return 'pi-cog pi-spin';
      case 'Available':
        return 'pi-check-circle';
      case 'OutOfService':
        return 'pi-times-circle';
      case 'Maintenance':
        return 'pi-wrench';
      default:
        return 'pi-question';
    }
  }

  private getColor(label: string): string {
    switch (label) {
      case 'Idle':
        return '#6c757d';
      case 'Loading':
        return '#ffc107';
      case 'Unloading':
        return '#17a2b8';
      case 'InUse':
        return '#007bff';
      case 'Available':
        return '#28a745';
      case 'OutOfService':
        return '#dc3545';
      case 'Maintenance':
        return '#fd7e14';
      default:
        return '#000000';
    }
  }

  private canEdit(): void {
    if (AuthService.hasRole(MachineDashboardRoles.MachineDashboardViewer)) {
      this.blockAutoCompleteInputs.set(true);
      this.blockSection.set(true);
    }
    if (AuthService.hasRole(MachineDashboardRoles.MachineDashboardEditor)) {
      this.blockAutoCompleteInputs.set(false);
      this.blockSection.set(false);
    }
  }
}
